import { useState, useCallback, useEffect } from 'react';
import { companiesApi } from '../../services/companiesApi';
import { plantsApi } from '../../services/plantsApi';
import { plantKilnsApi } from '../../services/plantKilnsApi';
import type { Company, Plant, PlantKiln } from '../../types/entities';

export interface UseMasterDataResult {
  companies: Company[];
  plants: Plant[];
  kilns: PlantKiln[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

/**
 * Laedt Company-/Plant-/PlantKiln-Collections in einem Erstladevorgang (UI-SPEC Punkt 1) --
 * Fetch-/Fehlermuster woertlich nach `PlantsPage.fetchData` (28-PATTERNS.md). Die gesamte
 * Anlagenliste liegt danach im Speicher; ein Auswahlwechsel loest keinen weiteren Netzaufruf aus.
 * `reload` wird nach jedem Speichern/Loeschen auf einer der drei Ebenen aufgerufen.
 */
export function useMasterData(): UseMasterDataResult {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [plants, setPlants] = useState<Plant[]>([]);
  const [kilns, setKilns] = useState<PlantKiln[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [companiesData, plantsData, kilnsData] = await Promise.all([
        companiesApi.getAll(),
        plantsApi.getAll(),
        plantKilnsApi.getAll(),
      ]);
      setCompanies(companiesData);
      setPlants(plantsData);
      setKilns(kilnsData);
    } catch {
      setError('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { companies, plants, kilns, loading, error, reload: fetchData };
}
